/**
 * Level 0 (Liminal Atrium) — declarative scene layout.
 *
 * Everything "where does it go / how big is it / how fast does it
 * move" lives here so LiminalAtrium.ts reads as wiring and not a
 * scatter of magic numbers. When you want to nudge a drop-cloth
 * position, add a new hero prop, or retune the dog's trigger radius,
 * edit this file — not the scene.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

/**
 * A single hero-prop GLB to instantiate and position in world space.
 * `path` is served by Vite from /assets so it must start with a slash.
 */
export interface HeroPropSpec {
  /** Debug name — also used as the hero_<name> TransformNode label. */
  name: string;
  /** Absolute URL (served from /public or /assets). */
  path: string;
  /** World-space position of the prop's root TransformNode. */
  position: Vector3;
  /** Optional rotation around Y. */
  rotationY?: number;
  /** Uniform scale multiplier. Defaults to 1. */
  scale?: number;
  /**
   * Whether this prop's meshes cast shadows. Architectural shells
   * (walls, floors, ceilings) should opt out so they don't self-shadow
   * the whole scene into mud. Defaults to true.
   */
  castShadows?: boolean;
}

/**
 * Per-mesh gear spin spec. The name match is a suffix regex against
 * the mesh name, so `Clone of <name>` style imports still resolve.
 */
export interface GearSpinSpec {
  /** Regex fragment anchored to the end of the mesh name. */
  nameSuffix: RegExp;
  /** Radians per second. Signs and magnitudes hand-tuned. */
  speed: number;
}

/**
 * Trigger volume for the sleeping-guardian-dog wake encounter.
 * Minna entering the volume without Shadow Slip active triggers a
 * respawn. X/Y are checked as a 2D circle; Z is ignored (the playfield
 * is effectively 2.5D).
 */
export interface DogWakeSpec {
  center: Vector3;
  radius: number;
  /** Seconds of cooldown after a trigger before it can fire again. */
  cooldownSeconds: number;
  /** Toast message shown when the dog wakes. */
  wakeToastMessage: string;
}

/** Shadow-zone box as consumed by `createShadowZones`. */
export interface ShadowZoneSpec {
  position: Vector3;
  width: number;
  height: number;
  depth: number;
}

export interface LiminalAtriumLayout {
  heroProps: HeroPropSpec[];
  gearSpin: GearSpinSpec[];
  dogWake: DogWakeSpec;
  shadowZones: ShadowZoneSpec[];
}

const GLB = (name: string): string =>
  `/assets/models/environment/level0/${name}.glb`;

export const LIMINAL_ATRIUM_LAYOUT: LiminalAtriumLayout = {
  heroProps: [
    // Architectural shell sits behind the playfield and closes off
    // the silhouette. Very large; no shadow casting (it's the room).
    { name: "sunroomShell", path: GLB("sunroom_shell"),
      position: new Vector3(0, 0, 0), castShadows: false },

    // Three window bays spaced across the back wall.
    { name: "window_L", path: GLB("leaded_glass_window_bay"),
      position: new Vector3(-12, 5.2, -2.45) },
    { name: "window_C", path: GLB("leaded_glass_window_bay"),
      position: new Vector3(0, 5.2, -2.45) },
    { name: "window_R", path: GLB("leaded_glass_window_bay"),
      position: new Vector3(12, 5.2, -2.45) },

    // Plank floor with two rectangular cutouts for the gearboxes.
    { name: "atriumFloor", path: GLB("wooden_floor_with_gear_cutout"),
      position: new Vector3(0, 0, -0.5), castShadows: false },

    // Two brass gearboxes showing through the floor cutouts. Gear
    // spin speeds are in LIMINAL_ATRIUM_LAYOUT.gearSpin below.
    { name: "gearbox_L", path: GLB("brass_floor_gearbox"),
      position: new Vector3(-4, 0, -0.5), castShadows: false },
    { name: "gearbox_R", path: GLB("brass_floor_gearbox"),
      position: new Vector3(8, 0, -0.5), castShadows: false },

    // Ornate Victorian grandfather clock (Sketchfab) standing on the
    // floor between the first two window bays.
    { name: "pendulumClock", path: GLB("pendulum_wall_clock"),
      position: new Vector3(-9.5, 0, -2.1), castShadows: false },

    // Drop-cloth furniture scattered along the back z-plane.

    // (crystal_vine_portal_door.glb is loaded inside Portal.ts via
    //  loadModelOverride so it tracks the Portal entity's position.)

    // Guardian German Shepherd moved out of the hero-prop list: it is now
    // an active NPC (`GuardianDog`) loaded with its own authored model at
    // `/assets/models/npcs/guardian_dog_sleeping.glb`. See
    // `scenes/LiminalAtrium.ts` for instantiation.
    //
    // The legacy `sleeping_dog.glb` is retained in `assets/models/
    // environment/level0/` as a superseded asset (it was a passive prop
    // that paired with the removed "dog wake" rug trigger); any future
    // level that needs a background sleeping dog can still reference it.
  ],

  gearSpin: [
    // Three different speeds per gearbox so it reads as mechanism
    // rather than "three gears wobbling in lockstep."
    { nameSuffix: /Gearbox_GearLarge$/,  speed:  0.22 },
    { nameSuffix: /Gearbox_GearMedium$/, speed: -0.32 },
    { nameSuffix: /Gearbox_GearSmall$/,  speed:  0.55 },
  ],

  // The dog's center matches the sleepingDog hero-prop position plus
  // a small Y offset so the circle sits on the body, not the floor.
  dogWake: {
    center: new Vector3(-6.5, 0.5, -0.3),
    radius: 1.1,
    cooldownSeconds: 2.0,
    wakeToastMessage: "The guardian dog stirs — use Shadow Slip to pass",
  },

  // Shadow zones line up with the drop-cloth furniture and the dog
  // rug so the concept-art "shadow pools under furniture" reads.
  shadowZones: [
    { position: new Vector3(-11, 0.6, 0), width: 3.2, height: 1.2, depth: 1.5 },
    { position: new Vector3(-6.5, 0.4, 0), width: 2.6, height: 0.9, depth: 1.5 },
    { position: new Vector3(3, 1.2, 0),    width: 3.4, height: 2.0, depth: 1.5 },
  ],
};
